import type { AssetsQueryDto } from '@terra/shared-contracts-assets';
import type { EndpointPreviewReturnDto } from '@terra/shared-contracts-endpoints';
import type { WebPageWithSignedScreenshotReturnDto } from '@terra/shared-contracts-web-pages';
import {
  APPLICATION_LEVEL_NODE_ID,
  DomainTreeNodeType,
  addApplicationLevelRoot as addApplicationLevelRootShared,
  buildDomainTree as buildDomainTreeShared,
  collectDirectEndpointIds,
  collectDirectWebPageIds,
  decodeUriComponentSafely,
  type DomainTree as SharedDomainTree,
  type DomainTreeBuildEndpoint,
  type DomainTreeBuildWebPage,
  type DomainTreeNode as SharedDomainTreeNode,
} from '@terra/shared-utils';
import { isArchivedLifecycle } from '@terra/shared-dto-types';
import { isEndpointAsset, type AssetDrawerDataPreview } from '../types';
import { getEndpointMethodLabel } from '../../../utils/endpoint-asset-display.utils';

export { APPLICATION_LEVEL_NODE_ID, DomainTreeNodeType };

export type DomainTreeNode = {
  id: string;
  name: string;
  fullDomain: string;
  level: number;
  nodeType: DomainTreeNodeType;
  children: DomainTreeNode[];
  parent?: DomainTreeNode;
  assets: AssetDrawerDataPreview[];
  directEndpointIds: string[];
  directWebPageIds: string[];
  isExpanded: boolean;
  assetCount: number;
  fullPath?: string;
  method?: string;
  assetId?: string;
  isArchived?: boolean;
};

export type DomainTree = {
  roots: DomainTreeNode[];
  nodeMap: Map<string, DomainTreeNode>;
};

function toBuildEndpoint(
  endpoint: EndpointPreviewReturnDto,
): DomainTreeBuildEndpoint {
  return {
    id: endpoint.id,
    url: endpoint.url,
    path: endpoint.path,
    method: getEndpointMethodLabel(endpoint),
    graphqlOperationName: endpoint.graphqlOperationName,
    xmlOperationName: endpoint.xmlOperationName,
    xmlOperationDisplayName: endpoint.xmlOperationDisplayName,
    isArchived: isArchivedLifecycle(endpoint.lifecycle),
  };
}

type WebPageBuildInput = {
  id: string;
  url: string;
  role?: string | null;
  lifecycle?: { state: string } | null;
};

function toBuildWebPage(webPage: WebPageBuildInput): DomainTreeBuildWebPage {
  return {
    id: webPage.id,
    url: webPage.url,
    role: webPage.role ?? undefined,
    isArchived: isArchivedLifecycle(webPage.lifecycle),
  };
}

function isWebPageWithSignedScreenshot(
  data: AssetDrawerDataPreview['data'],
): data is WebPageWithSignedScreenshotReturnDto {
  return (
    typeof (data as WebPageWithSignedScreenshotReturnDto).applicationId ===
    'string'
  );
}

function toUiAsset(
  asset: SharedDomainTreeNode['assets'][number],
  endpointsById: Map<string, EndpointPreviewReturnDto>,
  webPagesById: Map<string, WebPageWithSignedScreenshotReturnDto>,
): AssetDrawerDataPreview | undefined {
  if (asset.type === 'endpoint') {
    const endpoint = endpointsById.get(asset.data.id);

    if (!endpoint) {
      return undefined;
    }

    return { type: 'endpoint', data: endpoint };
  }

  const webPage = webPagesById.get(asset.data.id);

  if (!webPage) {
    return undefined;
  }

  return { type: 'webpage', data: webPage };
}

function hydrateUiNode({
  sharedNode,
  endpointsById,
  webPagesById,
  parent,
}: {
  sharedNode: SharedDomainTreeNode;
  endpointsById: Map<string, EndpointPreviewReturnDto>;
  webPagesById: Map<string, WebPageWithSignedScreenshotReturnDto>;
  parent?: DomainTreeNode;
}): DomainTreeNode {
  const node: DomainTreeNode = {
    id: sharedNode.id,
    name: sharedNode.name,
    fullDomain: sharedNode.fullDomain,
    level: sharedNode.level,
    nodeType: sharedNode.nodeType,
    children: [],
    parent,
    assets: sharedNode.assets
      .map((asset) => toUiAsset(asset, endpointsById, webPagesById))
      .filter((asset): asset is AssetDrawerDataPreview => asset !== undefined),
    directEndpointIds: collectDirectEndpointIds(sharedNode),
    directWebPageIds: collectDirectWebPageIds(sharedNode),
    isExpanded: sharedNode.isExpanded,
    assetCount: sharedNode.assetCount,
    fullPath: sharedNode.fullPath,
    method: sharedNode.method,
    assetId: sharedNode.assetId,
    isArchived: sharedNode.isArchived === true,
  };

  node.children = sharedNode.children.map((child) =>
    hydrateUiNode({
      sharedNode: child,
      endpointsById,
      webPagesById,
      parent: node,
    }),
  );

  return node;
}

function toUiDomainTree({
  sharedTree,
  endpoints,
  webPages,
}: {
  sharedTree: SharedDomainTree;
  endpoints: EndpointPreviewReturnDto[];
  webPages: WebPageWithSignedScreenshotReturnDto[];
}): DomainTree {
  const endpointsById = new Map(
    endpoints.map((endpoint) => [endpoint.id, endpoint]),
  );
  const webPagesById = new Map(
    webPages.map((webPage) => [webPage.id, webPage]),
  );
  const nodeMap = new Map<string, DomainTreeNode>();
  const roots = sharedTree.roots.map((root) => {
    const uiRoot = hydrateUiNode({
      sharedNode: root,
      endpointsById,
      webPagesById,
    });
    const registerNodes = (node: DomainTreeNode) => {
      nodeMap.set(node.id, node);
      node.children.forEach(registerNodes);
    };

    registerNodes(uiRoot);

    return uiRoot;
  });

  return { roots, nodeMap };
}

export function buildDomainTree(
  endpoints: EndpointPreviewReturnDto[],
  webPages: WebPageWithSignedScreenshotReturnDto[],
  options?: {
    includeMethodNodes?: boolean;
    applyApplicationLevelRoot?: boolean;
  },
): DomainTree {
  const sharedTree = buildDomainTreeShared(
    endpoints.map(toBuildEndpoint),
    webPages.map(toBuildWebPage),
    options,
  );

  return toUiDomainTree({ sharedTree, endpoints, webPages });
}

export function addApplicationLevelRoot(tree: DomainTree): DomainTree {
  const sharedRoots = tree.roots.map(toSharedNodeFromUi);
  const sharedNodeMap = new Map(
    [...tree.nodeMap.values()].map((node) => [
      node.id,
      toSharedNodeFromUi(node),
    ]),
  );
  const sharedTree = addApplicationLevelRootShared({
    roots: sharedRoots,
    nodeMap: sharedNodeMap,
  });

  return toUiDomainTree({
    sharedTree,
    endpoints: collectEndpointsFromUiTree(tree),
    webPages: collectWebPagesFromUiTree(tree),
  });
}

function toSharedNodeFromUi(node: DomainTreeNode): SharedDomainTreeNode {
  return {
    id: node.id,
    name: node.name,
    fullDomain: node.fullDomain,
    level: node.level,
    nodeType: node.nodeType,
    children: node.children.map(toSharedNodeFromUi),
    assets: node.assets.map((asset) => {
      if (isEndpointAsset(asset)) {
        return {
          type: 'endpoint' as const,
          data: toBuildEndpoint(asset.data),
        };
      }

      return {
        type: 'webpage' as const,
        data: toBuildWebPage(asset.data),
      };
    }),
    isExpanded: node.isExpanded,
    assetCount: node.assetCount,
    fullPath: node.fullPath,
    method: node.method,
    assetId: node.assetId,
  };
}

function collectEndpointsFromUiTree(
  tree: DomainTree,
): EndpointPreviewReturnDto[] {
  const endpoints = new Map<string, EndpointPreviewReturnDto>();

  tree.nodeMap.forEach((node) => {
    node.assets.forEach((asset) => {
      if (isEndpointAsset(asset)) {
        endpoints.set(asset.data.id, asset.data);
      }
    });
  });

  return [...endpoints.values()];
}

function collectWebPagesFromUiTree(
  tree: DomainTree,
): WebPageWithSignedScreenshotReturnDto[] {
  const webPages = new Map<string, WebPageWithSignedScreenshotReturnDto>();

  tree.nodeMap.forEach((node) => {
    node.assets.forEach((asset) => {
      if (
        !isEndpointAsset(asset) &&
        isWebPageWithSignedScreenshot(asset.data)
      ) {
        webPages.set(asset.data.id, asset.data);
      }
    });
  });

  return [...webPages.values()];
}

function cloneTreeWithExpansionUpdate(
  tree: DomainTree,
  setExpanded: (node: DomainTreeNode) => boolean,
): DomainTree {
  const newNodeMap = new Map<string, DomainTreeNode>();

  tree.nodeMap.forEach((node) => {
    newNodeMap.set(node.id, {
      ...node,
      children: [],
      parent: undefined,
      isExpanded: setExpanded(node),
    });
  });
  newNodeMap.forEach((node) => {
    const original = tree.nodeMap.get(node.id);

    if (original?.parent) {
      const parent = newNodeMap.get(original.parent.id);

      if (parent) node.parent = parent;
    }

    if (original?.children?.length) {
      node.children = original.children
        .map((child) => newNodeMap.get(child.id))
        .filter((child): child is DomainTreeNode => child !== undefined);
    }
  });
  const roots = tree.roots
    .map((root) => newNodeMap.get(root.id))
    .filter((root): root is DomainTreeNode => root !== undefined);

  return { roots, nodeMap: newNodeMap };
}

export function toggleNodeExpanded(
  tree: DomainTree,
  nodeId: string,
): DomainTree {
  return cloneTreeWithExpansionUpdate(tree, (node) =>
    node.id === nodeId ? !node.isExpanded : node.isExpanded,
  );
}

export function expandAllNodes(tree: DomainTree): DomainTree {
  return cloneTreeWithExpansionUpdate(tree, () => true);
}

export function collapseAllNodes(tree: DomainTree): DomainTree {
  return cloneTreeWithExpansionUpdate(tree, () => false);
}

export function getDescendantIds(node: DomainTreeNode): string[] {
  const ids: string[] = [];

  function collect(n: DomainTreeNode) {
    n.children.forEach((child) => {
      ids.push(child.id);
      collect(child);
    });
  }

  collect(node);

  return ids;
}

export function getAncestorIds(node: DomainTreeNode): string[] {
  const ids: string[] = [];
  let current = node.parent;

  while (current) {
    ids.push(current.id);
    current = current.parent;
  }

  return ids;
}

export function collectSubtreeEndpointIds(node: DomainTreeNode): string[] {
  const endpointIds = new Set<string>();

  const visit = (currentNode: DomainTreeNode) => {
    currentNode.directEndpointIds.forEach((endpointId) => {
      endpointIds.add(endpointId);
    });

    currentNode.assets.forEach((asset) => {
      if (isEndpointAsset(asset)) {
        endpointIds.add(asset.data.id);
      }
    });

    currentNode.children.forEach(visit);
  };

  visit(node);

  return [...endpointIds];
}

function collectSubtreeAssetIds(node: DomainTreeNode): {
  endpointIds: Set<string>;
  webPageIds: Set<string>;
} {
  const endpointIds = new Set(collectSubtreeEndpointIds(node));
  const webPageIds = new Set<string>();

  const visit = (currentNode: DomainTreeNode) => {
    currentNode.directWebPageIds.forEach((webPageId) => {
      webPageIds.add(webPageId);
    });

    currentNode.assets.forEach((asset) => {
      if (!isEndpointAsset(asset)) {
        webPageIds.add(asset.data.id);
      }
    });

    currentNode.children.forEach(visit);
  };

  visit(node);

  return { endpointIds, webPageIds };
}

function matchesAssetUrlPatterns({
  asset,
  urlRegexPatterns,
}: {
  asset: AssetDrawerDataPreview;
  urlRegexPatterns: RegExp[];
}): boolean {
  const url = asset.data.url;

  if (urlRegexPatterns.some((regex) => regex.test(url))) {
    return true;
  }

  const graphqlOperationName = isEndpointAsset(asset)
    ? asset.data.graphqlOperationName
    : undefined;

  if (graphqlOperationName) {
    return urlRegexPatterns.some((regex) => regex.test(graphqlOperationName));
  }

  return false;
}

function filterAssetsByUrlPatterns({
  allAssets,
  selectedNodeIds,
  tree,
}: {
  allAssets: AssetDrawerDataPreview[];
  selectedNodeIds: string[];
  tree: DomainTree;
}): AssetDrawerDataPreview[] {
  const urlPatterns = generateUrlPatternsFromTreeSelection(
    selectedNodeIds,
    tree,
  );

  if (urlPatterns.length === 0) {
    return [];
  }

  const urlRegexPatterns = urlPatterns.flatMap((pattern) => {
    try {
      return [new RegExp(pattern)];
    } catch {
      return [];
    }
  });

  if (urlRegexPatterns.length === 0) {
    return [];
  }

  return allAssets.filter((asset) =>
    matchesAssetUrlPatterns({ asset, urlRegexPatterns }),
  );
}

export function getNodeAssets({
  node,
  allAssets,
}: {
  node: DomainTreeNode;
  allAssets?: AssetDrawerDataPreview[];
}): AssetDrawerDataPreview[] {
  if (!allAssets) {
    const assets = [...node.assets];

    const collectAssetsFromChildren = (parentNode: DomainTreeNode): void => {
      parentNode.children.forEach((child) => {
        assets.push(...child.assets);
        collectAssetsFromChildren(child);
      });
    };

    collectAssetsFromChildren(node);

    return assets;
  }

  const { endpointIds, webPageIds } = collectSubtreeAssetIds(node);

  return allAssets.filter((asset) => {
    if (isEndpointAsset(asset)) {
      return endpointIds.has(asset.data.id);
    }

    return webPageIds.has(asset.data.id);
  });
}

export function filterAssetsByDomainSelection(
  allAssets: AssetDrawerDataPreview[],
  selectedNodeIds: string[],
  tree: DomainTree,
): AssetDrawerDataPreview[] {
  if (selectedNodeIds.length === 0) {
    return allAssets;
  }

  const selectedEndpointIds = new Set<string>();
  const selectedWebPageIds = new Set<string>();

  selectedNodeIds.forEach((nodeId) => {
    const node = tree.nodeMap.get(nodeId);

    if (!node) {
      return;
    }

    const { endpointIds, webPageIds } = collectSubtreeAssetIds(node);

    endpointIds.forEach((endpointId) => selectedEndpointIds.add(endpointId));
    webPageIds.forEach((webPageId) => selectedWebPageIds.add(webPageId));
  });

  const hasResolvedAssetIds =
    selectedEndpointIds.size > 0 || selectedWebPageIds.size > 0;

  if (hasResolvedAssetIds) {
    return allAssets.filter((asset) => {
      if (isEndpointAsset(asset)) {
        return selectedEndpointIds.has(asset.data.id);
      }

      return selectedWebPageIds.has(asset.data.id);
    });
  }

  return filterAssetsByUrlPatterns({ allAssets, selectedNodeIds, tree });
}

export function searchDomainTree(
  tree: DomainTree,
  searchTerm: string,
): string[] {
  const lowerSearchTerm = searchTerm.toLowerCase();
  const matchingNodeIds: string[] = [];

  tree.nodeMap.forEach((node, nodeId) => {
    const fullUrl = node.fullPath
      ? `${node.fullDomain}${node.fullPath}`.toLowerCase()
      : node.fullDomain.toLowerCase();

    const matches =
      node.name.toLowerCase().includes(lowerSearchTerm) ||
      node.fullDomain.toLowerCase().includes(lowerSearchTerm) ||
      node.fullPath?.toLowerCase().includes(lowerSearchTerm) ||
      node.method?.toLowerCase().includes(lowerSearchTerm) ||
      fullUrl.includes(lowerSearchTerm);

    if (matches) {
      matchingNodeIds.push(nodeId);
    }
  });

  return matchingNodeIds;
}

export function generateUrlPatternsFromTreeSelection(
  selectedNodeIds: string[],
  domainTree: DomainTree,
): string[] {
  const patterns: string[] = [];
  const processedPaths = new Set<string>();

  const buildPathPattern = (fullPath: string): string => {
    const decodedPath = decodeUriComponentSafely(fullPath);
    const normalizedPath = decodedPath.startsWith('/')
      ? decodedPath
      : `/${decodedPath}`;
    const segments = normalizedPath.split('/').filter(Boolean);

    if (segments.length === 0) {
      return '.*://[^/]+/($|[/?#])';
    }

    const segmentPattern = segments
      .map((segment) => {
        return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('/');

    return `.*://[^/]+/${segmentPattern}($|[/?#])`;
  };

  selectedNodeIds.forEach((nodeId) => {
    if (nodeId === APPLICATION_LEVEL_NODE_ID) return;
    const node = domainTree.nodeMap.get(nodeId);

    if (!node) return;

    if (node.nodeType === DomainTreeNodeType.Domain) {
      if (!processedPaths.has(node.fullDomain)) {
        const domainPattern = `.*${node.fullDomain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*`;

        patterns.push(domainPattern);
        processedPaths.add(node.fullDomain);
      }
    } else if (node.nodeType === DomainTreeNodeType.Path) {
      if (node.fullPath) {
        if (!processedPaths.has(node.fullPath)) {
          const pathPattern = buildPathPattern(node.fullPath);

          patterns.push(pathPattern);
          processedPaths.add(node.fullPath);
        }
      }
    } else if (
      node.nodeType === DomainTreeNodeType.Endpoint ||
      node.nodeType === DomainTreeNodeType.Webpage
    ) {
      if (node.fullPath) {
        if (!processedPaths.has(node.fullPath)) {
          const endpointPattern = buildPathPattern(node.fullPath);

          patterns.push(endpointPattern);
          processedPaths.add(node.fullPath);
        }
      }
    }
  });

  return patterns;
}

type DomainFilter = NonNullable<AssetsQueryDto['domainFilters']>[number];

export function hasScopedDomainTreeSelection(
  selectedNodeIds: string[],
): boolean {
  return selectedNodeIds.some((nodeId) => nodeId !== APPLICATION_LEVEL_NODE_ID);
}

export function isDomainTreeFilterPending({
  selectedNodeIds,
  domainTree,
}: {
  selectedNodeIds: string[];
  domainTree: DomainTree | null | undefined;
}): boolean {
  return hasScopedDomainTreeSelection(selectedNodeIds) && !domainTree;
}

export function buildDomainFiltersFromNodeMap(
  nodeIds: string[],
  nodeMap: Map<string, DomainTreeNode>,
): DomainFilter[] | undefined {
  if (nodeIds.length === 0) return undefined;

  if (nodeIds.some((id) => id === APPLICATION_LEVEL_NODE_ID)) {
    return undefined;
  }

  const filters: DomainFilter[] = [];

  for (const nodeId of nodeIds) {
    const node = nodeMap.get(nodeId);

    if (!node) continue;

    const filter: DomainFilter = { host: node.fullDomain };

    if (node.fullPath) {
      filter.pathPrefix = decodeUriComponentSafely(node.fullPath);
    }

    if (node.nodeType === DomainTreeNodeType.Endpoint && node.method) {
      filter.method = node.method as DomainFilter['method'];
    }

    filters.push(filter);
  }

  return filters.length > 0 ? filters : undefined;
}

export function isDescendantSelected(
  node: DomainTreeNode,
  selectedDomains: ReadonlySet<string>,
): boolean {
  if (!node.children || node.children.length === 0) {
    return selectedDomains.has(node.id);
  }

  return node.children.some(
    (child) =>
      selectedDomains.has(child.id) ||
      isDescendantSelected(child, selectedDomains),
  );
}

export function matchesNodeSearch({
  node,
  searchTerm,
}: {
  node: DomainTreeNode;
  searchTerm: string;
}): boolean {
  if (!searchTerm) return true;

  const s = searchTerm.toLowerCase();
  const decodedName = decodeUriComponentSafely(node.name).toLowerCase();
  const decodedFullPath = node.fullPath
    ? decodeUriComponentSafely(node.fullPath).toLowerCase()
    : '';

  const fullUrl = decodedFullPath
    ? `${node.fullDomain}${decodedFullPath}`.toLowerCase()
    : node.fullDomain.toLowerCase();

  return (
    decodedName.includes(s) ||
    node.name.toLowerCase().includes(s) ||
    (node.fullDomain?.toLowerCase().includes(s) ?? false) ||
    decodedFullPath.includes(s) ||
    (node.method?.toLowerCase().includes(s) ?? false) ||
    fullUrl.includes(s)
  );
}

export function matchesDescendantSearch({
  node,
  searchTerm,
}: {
  node: DomainTreeNode;
  searchTerm: string;
}): boolean {
  if (!searchTerm) return false;

  const dfs = (n: DomainTreeNode): boolean => {
    if (!n.children || n.children.length === 0) return false;

    for (const child of n.children) {
      if (matchesNodeSearch({ node: child, searchTerm })) {
        return true;
      }

      if (dfs(child)) {
        return true;
      }
    }

    return false;
  };

  return dfs(node);
}

export type VisibleNode = { node: DomainTreeNode; level: number };

export function getDomainTreeStructureSignature(tree: DomainTree): string {
  const parts: string[] = [];

  const visit = (node: DomainTreeNode) => {
    parts.push(
      `${node.id}|${node.nodeType}|${node.name}|${node.assetCount}|${node.isArchived ? '1' : '0'}|${node.children.map((child) => child.id).join(',')}`,
    );
    node.children.forEach(visit);
  };

  tree.roots.forEach(visit);

  return parts.join(';');
}

export function getVisibleNodes(
  roots: DomainTreeNode[],
  searchTerm: string,
): VisibleNode[] {
  const result: VisibleNode[] = [];

  function visit(nodes: DomainTreeNode[], level: number) {
    for (const node of nodes) {
      const matchesSelf = matchesNodeSearch({ node, searchTerm });
      const matchesDescendant = matchesDescendantSearch({ node, searchTerm });

      if (matchesSelf || matchesDescendant) {
        result.push({ node, level });
      }

      if (node.children && node.children.length > 0 && node.isExpanded) {
        visit(node.children, level + 1);
      }
    }
  }

  visit(roots, 0);

  return result;
}
